import React, { useEffect } from 'react';
import { TileGrid } from '@apps/card/components/TileGrid';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import { PageSection } from '@apps/card/components/PageSection';
import { useCardTelemetry, TelemetryProvider, enrichCardTelemetryExtraInfo } from '@apps/card/Card.telemetry';
import { useDesignTemplates } from './useDesignTemplates';
import { useDesignGalleryItems } from './useDesignGalleryItems';
import type { CustomizeNowClickOverrideHandler, StationeryTemplate } from '@apps/card/types/stationeryTemplate.types';
import { GalleryItem } from './components/GalleryItem/GalleryItem';
import { useConciergeDisclosure } from './useConciergeDisclosure';
import { BackToTop } from '@shared/components/BackToTop';
import type { FilterFacet } from './components/Filters/Filter.types';
import { DesignGalleryFiltersProvider } from './DesignGalleryFiltersProvider';
import { SearchAndFiltersContainer } from './components/Filters/SearchAndFilterContainer';
import { useQueryParamHelper } from '@shared/core/queryString';
import { GallerySeo } from './DesignsGallerySeo';
import { CardFormat } from '../ProductDetailsPage/constants';
import { useFreeShippingBanner } from '@apps/card/hooks/useFreeShippingBanner';
import { ItemImpressionsTelemetryProvider } from './components/ItemImpressionsTelemetry';
import { FaqSection } from '@apps/card/routes/PublicShop/components/FaqSection';
import { useStationeryFaqItems } from '@apps/card/routes/PublicShop/components/FaqSection/useStationeryFaqItems';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { SeoContentSection } from '@apps/card/routes/PublicShop/components/SeoContentSection';
import { RelatedLinks } from '@apps/card/routes/PublicShop/components/RelatedLinks';
import { SeoReviewsSection } from '@apps/card/routes/PublicShop/components/SeoReviewsSection';
import { BreadcrumbSection } from './components/BreadcrumbSection';
import { useFilterFacets } from './useFilterFacets';
import { useProductsWithStatus } from '@apps/card/hooks/useProducts';
import { DesignsGallerySkeleton } from './DesignsGallerySkeleton';
import { NoResultsFound } from './NoResultsFound';
import { GalleryColorwayOrderProvider } from './GalleryColorwayOrderContext';
import { GallerySearchScoresProvider } from './GallerySearchScoresContext';
import type { ColorwayColorHit } from './useColorwayColorHits';
import { useColorwayColorHits } from './useColorwayColorHits';
import { StyleShowcase } from './components/StyleShowcase';
import { GalleryPhotoPreview } from './GalleryPhotoPreview';
import { SaveTheDateMarcomFooter } from './SaveTheDateMarcomFooter.lazy';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';

type DesignsGalleryProps = Readonly<{
  eventId?: string;
  eventHandle?: string;
  // Intended only to be used for the investor demo.
  onCustomizeCardClickOverride?: CustomizeNowClickOverrideHandler;
}>;

const Root = ({
  filterFacets,
  eventId, // Only used for FavoriteBadge
  eventHandle,
  onCustomizeCardClickOverride,
  templates,
  colorwayColorHits,
}: {
  filterFacets: FilterFacet[];
  templates: ReadonlyArray<StationeryTemplate>;
  colorwayColorHits: ReadonlyArray<ColorwayColorHit>;
} & DesignsGalleryProps) => {
  useCardTelemetry({ page: 'homeRoute' });
  const { currentCategory } = useCardCategoryContext();
  enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: currentCategory });
  useEffect(() => {
    enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: null });
  }, []);

  const { items, hasNoSearchResults, colorwayOrder, searchScoresByItem } = useDesignGalleryItems({ templates, colorwayColorHits });

  const { ConciergeDialog, onCardSelect } = useConciergeDisclosure(onCustomizeCardClickOverride);

  const params = useQueryParamHelper();
  const preferredFormat = (params.getValue('format') as CardFormat | undefined) ?? CardFormat.paper;

  return (
    <GalleryPhotoPreview
      eventId={eventId}
      eventHandle={eventHandle}
    >
      {previewAction => (
        <>
          {ConciergeDialog}
          <PageSection
            id="designs-gallery"
            paddingX={0}
            titleMarginX={{ _: 6, md: 8 }}
          >
            <SearchAndFiltersContainer
              filterFacets={filterFacets}
              trailingAction={previewAction}
            />
            {(() => {
              if (hasNoSearchResults) return <NoResultsFound />;

              return (
                <GalleryColorwayOrderProvider value={colorwayOrder}>
                  <GallerySearchScoresProvider value={searchScoresByItem}>
                    <TileGrid>
                      {items.map((data, index) => (
                        <GalleryItem
                          preferredFormat={preferredFormat}
                          cardTileSource="shop"
                          eventId={eventId}
                          onCardSelect={onCardSelect}
                          key={data.type === 'template' ? (data.instanceKey ?? data.id) : index}
                          data={data}
                          currentCategory={currentCategory}
                          index={index}
                        />
                      ))}
                    </TileGrid>
                  </GallerySearchScoresProvider>
                </GalleryColorwayOrderProvider>
              );
            })()}
          </PageSection>
        </>
      )}
    </GalleryPhotoPreview>
  );
};

export const DesignsGallery = ({ eventId, eventHandle, onCustomizeCardClickOverride }: DesignsGalleryProps) => {
  const { currentCategory } = useCardCategoryContext();
  const templates = useDesignTemplates({ eventId });
  // Computed once here and threaded down, so the color facet's counts and the
  // filtering they describe are derived from the same colorway matches.
  const colorwayColorHits = useColorwayColorHits(templates);
  const filterFacets = useFilterFacets(templates, colorwayColorHits);

  const { isPublicShopRoute } = useCardsRouterContext();
  const { products, isLoading: isLoadingProducts } = useProductsWithStatus();
  useFreeShippingBanner(products, !isLoadingProducts);
  const faqItems = useStationeryFaqItems(currentCategory);

  return (
    <>
      <GallerySeo
        currentCategory={currentCategory}
        faqItems={faqItems}
        isPublicShopRoute={isPublicShopRoute}
      />
      {isPublicShopRoute && <BreadcrumbSection category={currentCategory} />}
      <TelemetryProvider>
        <DesignGalleryFiltersProvider>
          {/* Static content — render it without waiting for templates. */}
          <StyleShowcase category={currentCategory} />
          <SkeletonGroup
            isReady={!!templates.length}
            placeholder={<DesignsGallerySkeleton />}
          >
            <ItemImpressionsTelemetryProvider>
              <Root
                templates={templates}
                colorwayColorHits={colorwayColorHits}
                eventId={eventId}
                eventHandle={eventHandle}
                onCustomizeCardClickOverride={onCustomizeCardClickOverride}
                filterFacets={filterFacets}
              />
              <BackToTop />
            </ItemImpressionsTelemetryProvider>
          </SkeletonGroup>
        </DesignGalleryFiltersProvider>
      </TelemetryProvider>
      {isPublicShopRoute && <RelatedLinks category={currentCategory} />}
      <FaqSection
        faqItems={faqItems}
        category={currentCategory}
      />
      <SeoReviewsSection category={currentCategory} />
      <SeoContentSection category={currentCategory} />
      {isPublicShopRoute && currentCategory === StationeryTemplateCategoryEnum.saveTheDate && <SaveTheDateMarcomFooter />}
    </>
  );
};
